/**
 * Phase 101 — Document Composer.
 *
 * Three-step modal:
 *   1. Pick a template (auto-filtered to the target type — student vs employee).
 *   2. Fill addressed_to + subject + body (pre-hydrated from backend /render).
 *   3. Preview inside the LetterheadShell + Save & Print.
 *
 * Props:
 *   open: bool
 *   onClose(): close handler
 *   targetType: 'student' | 'employee'
 *   target: { id, name, custom_id, ... }
 *   academy: academy object (for the letterhead header)
 *   onSaved(savedDoc): called after successful save
 */
import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileText, Printer, Save, ArrowLeft, ArrowRight, Loader2, Sparkles, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { LetterheadShell, printLetterhead } from './LetterheadShell';
// Phase 246 — Single source of truth for customer-facing date strings.
import { formatGlobalDate } from '@/utils/dateFormatter';

export function DocumentComposer({ open, onClose, targetType, target, academy, onSaved }) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signingAuthority, setSigningAuthority] = useState('');
  const [doc, setDoc] = useState({ subject: '', addressed_to: '', body_text: '' });
  // Template-specific extras (tournament_name, exit_date, etc.)
  const [customData, setCustomData] = useState({});

  useEffect(() => {
    if (!open) return;
    setStep(1); setTemplateId(''); setDoc({ subject: '', addressed_to: '', body_text: '' });
    setSigningAuthority(''); setCustomData({});
  }, [open, targetType, target?.id]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get('/documents/templates');
        const list = (r.data?.templates || []).filter(t => t.target === targetType);
        setTemplates(list);
      } catch (err) {
        toast.error('Failed to load templates');
      }
    })();
  }, [open, targetType]);

  const hydratePreview = useCallback(async (tid, extra) => {
    if (!tid || !target?.id) return;
    setLoadingPreview(true);
    try {
      const r = await api.post('/documents/render', {
        template_id: tid,
        target_type: targetType,
        target_id: target.id,
        custom_data: extra || customData,
      });
      setDoc({
        subject: r.data?.subject || '',
        addressed_to: r.data?.addressed_to || '',
        body_text: r.data?.body_text || '',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to render template');
    } finally {
      setLoadingPreview(false);
    }
  }, [target?.id, targetType, customData]);

  const handlePickTemplate = async (tid) => {
    setTemplateId(tid);
    await hydratePreview(tid);
    setStep(2);
  };

  const handleSaveAndPrint = async (alsoPrint) => {
    if (!templateId) return;
    if (!doc.body_text.trim()) { toast.error('Body text cannot be empty'); return; }
    if (!signingAuthority.trim()) { toast.error('Signing authority is required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/documents', {
        template_id: templateId,
        target_type: targetType,
        target_id: target.id,
        addressed_to: doc.addressed_to,
        subject: doc.subject,
        body_text: doc.body_text,
        signing_authority: signingAuthority,
        custom_data: customData,
      });
      toast.success(`Document ${r.data.custom_id} saved to ledger`);
      onSaved?.(r.data);
      if (alsoPrint) {
        // Small delay so the letterhead is fully rendered before print()
        setTimeout(() => printLetterhead(), 250);
      } else {
        onClose?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  // Phase 246 — Save + download server-rendered PDF. We hit
  // `/documents/{id}/pdf` (Playwright A4) and trigger a browser download.
  // If chromium is unavailable in the deploy image, the backend transparently
  // serves an HTML print view with an `X-Pdf-Fallback` header — we surface a
  // toast hint and open it in a new tab so the user can still Cmd-P → PDF.
  const handleSaveAndDownloadPdf = async () => {
    if (!templateId) return;
    if (!doc.body_text.trim()) { toast.error('Body text cannot be empty'); return; }
    if (!signingAuthority.trim()) { toast.error('Signing authority is required'); return; }
    setSaving(true);
    try {
      // Step 1 — save first so the PDF has an immutable ledger row to render from.
      const r = await api.post('/documents', {
        template_id: templateId,
        target_type: targetType,
        target_id: target.id,
        addressed_to: doc.addressed_to,
        subject: doc.subject,
        body_text: doc.body_text,
        signing_authority: signingAuthority,
        custom_data: customData,
      });
      onSaved?.(r.data);
      // Step 2 — fetch the PDF as a blob (axios already carries the Bearer header).
      const pdfRes = await api.get(`/documents/${r.data.id}/pdf`, { responseType: 'blob' });
      const fallback = pdfRes.headers?.['x-pdf-fallback'] === 'html';
      const ct = pdfRes.headers?.['content-type'] || (fallback ? 'text/html' : 'application/pdf');
      const blob = new Blob([pdfRes.data], { type: ct });
      const url = URL.createObjectURL(blob);
      if (fallback) {
        // HTML fallback — open in new tab so the user can Cmd-P → Save as PDF.
        window.open(url, '_blank');
        toast.info('PDF engine offline — opened the printable HTML view in a new tab.', { duration: 5000 });
      } else {
        // Real PDF — trigger a clean attachment download.
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(r.data.template_label || 'Document').replace(/[^\w-]+/g, '_')}_${r.data.custom_id || r.data.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(`Downloaded ${r.data.custom_id}`);
      }
      // Revoke after a short delay so the new-tab case has time to read it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setSaving(false);
    }
  };

  const currentTemplate = templates.find(t => t.id === templateId);

  // Template-specific custom fields shown on step 2.
  const renderExtraFields = () => {
    if (templateId === 'tournament_permission') {
      return (
        <div className="grid grid-cols-2 gap-3 no-print">
          <div className="space-y-1"><Label className="text-xs">Tournament Name</Label>
            <Input value={customData.tournament_name || ''} onChange={(e) => setCustomData(prev => ({ ...prev, tournament_name: e.target.value }))} placeholder="e.g. Under-15 Hockey League" data-testid="composer-extra-tournament-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Tournament Date</Label>
            <Input type="date" value={customData.tournament_date || ''} onChange={(e) => setCustomData(prev => ({ ...prev, tournament_date: e.target.value }))} data-testid="composer-extra-tournament-date" /></div>
        </div>
      );
    }
    if (templateId === 'noc_certificate') {
      return (
        <div className="grid grid-cols-2 gap-3 no-print">
          <div className="space-y-1"><Label className="text-xs">Absence From</Label>
            <Input type="date" value={customData.absence_from || ''} onChange={(e) => setCustomData(prev => ({ ...prev, absence_from: e.target.value }))} /></div>
          <div className="space-y-1"><Label className="text-xs">Absence To</Label>
            <Input type="date" value={customData.absence_to || ''} onChange={(e) => setCustomData(prev => ({ ...prev, absence_to: e.target.value }))} /></div>
        </div>
      );
    }
    if (templateId === 'increment_letter') {
      return (
        <div className="grid grid-cols-2 gap-3 no-print">
          <div className="space-y-1"><Label className="text-xs">New Annual Salary (₹)</Label>
            <Input type="number" value={customData.new_salary || ''} onChange={(e) => setCustomData(prev => ({ ...prev, new_salary: e.target.value }))} /></div>
          <div className="space-y-1"><Label className="text-xs">Effective Date</Label>
            <Input type="date" value={customData.effective_date || ''} onChange={(e) => setCustomData(prev => ({ ...prev, effective_date: e.target.value }))} /></div>
        </div>
      );
    }
    if (templateId === 'experience_letter') {
      return (
        <div className="space-y-1 no-print">
          <Label className="text-xs">Exit Date</Label>
          <Input type="date" value={customData.exit_date || ''} onChange={(e) => setCustomData(prev => ({ ...prev, exit_date: e.target.value }))} />
        </div>
      );
    }
    if (templateId === 'release_certificate') {
      return (
        <div className="space-y-1 no-print">
          <Label className="text-xs">Release Date</Label>
          <Input type="date" value={customData.release_date || ''} onChange={(e) => setCustomData(prev => ({ ...prev, release_date: e.target.value }))} />
        </div>
      );
    }
    return null;
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="document-composer-dialog">
        <DialogHeader className="no-print">
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Compose Document — {target?.name || ''}</DialogTitle>
          <DialogDescription className="text-xs">{currentTemplate ? currentTemplate.label : 'Pick a template to start drafting.'} Step {step} of 3.</DialogDescription>
        </DialogHeader>

        {/* Step 1 — Template picker */}
        {step === 1 && (
          <div className="space-y-2 no-print">
            <p className="text-xs text-muted-foreground">Select the type of letter you want to issue to {target?.name || 'this profile'}.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {templates.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handlePickTemplate(t.id)}
                  data-testid={`composer-template-${t.id}`}
                  className="text-left p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition group"
                >
                  <p className="text-sm font-medium group-hover:text-primary">{t.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t.default_subject}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — Fill content */}
        {step === 2 && (
          <div className="space-y-3 no-print">
            {renderExtraFields()}
            {Object.keys(customData).length > 0 && (
              <Button type="button" variant="ghost" size="sm" disabled={loadingPreview} onClick={() => hydratePreview(templateId, customData)} className="h-7 text-xs">
                {loadingPreview ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />}
                Re-generate from filled fields
              </Button>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Addressed To</Label>
              <Textarea value={doc.addressed_to} onChange={(e) => setDoc(prev => ({ ...prev, addressed_to: e.target.value }))} rows={2} data-testid="composer-addressed-to" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject</Label>
              <Input value={doc.subject} onChange={(e) => setDoc(prev => ({ ...prev, subject: e.target.value }))} data-testid="composer-subject" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Body</Label>
              <Textarea value={doc.body_text} onChange={(e) => setDoc(prev => ({ ...prev, body_text: e.target.value }))} rows={11} className="font-serif" data-testid="composer-body" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Signing Authority</Label>
              <Input value={signingAuthority} onChange={(e) => setSigningAuthority(e.target.value)} placeholder="Name, Designation (e.g. Joseph Varakumar, President)" data-testid="composer-authority" />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <Button variant="ghost" size="sm" type="button" onClick={() => setStep(1)}><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Pick template</Button>
              <Button size="sm" type="button" onClick={() => setStep(3)} disabled={!doc.body_text.trim() || !signingAuthority.trim()} data-testid="composer-step3"><Eye className="h-3.5 w-3.5 mr-1" /> Preview &amp; Print</Button>
            </div>
          </div>
        )}

        {/* Step 3 — Letterhead preview + save & download (Phase 247.f — primary CTA is server-PDF) */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between no-print">
              <Button variant="ghost" size="sm" type="button" onClick={() => setStep(2)}><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Edit content</Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" type="button" disabled={saving} onClick={() => handleSaveAndPrint(false)} data-testid="composer-save-only">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save to Ledger
                </Button>
                <Button variant="outline" size="sm" type="button" disabled={saving} onClick={() => handleSaveAndPrint(true)} data-testid="composer-save-and-print" title="Uses your browser print dialog. Backgrounds and logos may render differently across devices.">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Printer className="h-3.5 w-3.5 mr-1.5" />} Print Now
                </Button>
                <div className="flex flex-col items-end">
                  <Button size="sm" type="button" disabled={saving} onClick={handleSaveAndDownloadPdf} data-testid="composer-save-and-download" className="bg-slate-950 hover:bg-slate-800 text-white">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />} Save &amp; Download PDF
                  </Button>
                  <span className="text-[10px] text-slate-400 mt-1 select-none">Recommended for best fidelity</span>
                </div>
              </div>
            </div>
            <LetterheadShell
              academy={academy}
              subject={doc.subject}
              addressedTo={doc.addressed_to}
              bodyText={doc.body_text}
              signingAuthority={signingAuthority}
              dateLabel={formatGlobalDate(new Date())}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
